import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { CheckboxGroup } from '@eika/checkbox';

import { useKunde } from '../../kundeinfo/kunde-context';
import { AppState } from '../../reducers';
import { Bygningforsikring } from '../../types/internal-types';
import { updateObjekterForForsikring, updateValgteObjekter } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { ObjekterCheckboxFeilmelding } from './forsikring-objekter-checkbox-feilmelding';
import {
    CurrentForsikringObjekterCheckboxSporsmal,
    findValgtForsikringWithObjekterFrom,
    getForsikringElementerAsAlternativerOrUpdatedCurrentSporsmalAlternativer,
    ObjektAlternativ,
} from './forsikring-objekter-checkbox-types';

import './objekter-checkbox-feilmelding.scss';

const annenBygningInntil10kvmObjektAlternativ = (previousStateOfObjekt?: ObjektAlternativ): ObjektAlternativ => ({
    objektnavn: 'Annen bygning på inntil 10 kvm',
    objektnummer: -1,
    checked: previousStateOfObjekt ? previousStateOfObjekt.checked : false,
});

function addOrUpdatePakrevdAnnenBygningInntil10kvmAlternativ(
    currentSporsmalObjekter: ObjektAlternativ[],
    objekter: { checked: boolean; objektnavn: string; objektnummer: number }[],
) {
    const besvartValgForPakrevdElement = currentSporsmalObjekter.find((o) => o.objektnummer === 1_000);

    objekter.push(annenBygningInntil10kvmObjektAlternativ(besvartValgForPakrevdElement));
}

export interface ForsikringObjekterCheckboxProps {
    currentSporsmal: CurrentForsikringObjekterCheckboxSporsmal;
    showFeilmelding: boolean;
}

export const ForsikringObjekterCheckbox = ({ currentSporsmal, showFeilmelding }: ForsikringObjekterCheckboxProps) => {
    const { sporsmalsflytState } = useSelector((state: AppState) => state.skademelding);
    const [valgtForsikringWithObjekter, setValgtForsikringWithObjekter] = useState<Bygningforsikring | null>(null);
    const [currentSporsmalObjekter] = useState<ObjektAlternativ[]>(currentSporsmal.objekter);

    const kunde = useKunde();

    const dispatch = useSporsmalsflytDispatch();

    useEffect(() => {
        if (sporsmalsflytState.forsikringIdentifikator === null) setValgtForsikringWithObjekter(null);
        else {
            const bygningforsikring = findValgtForsikringWithObjekterFrom(
                sporsmalsflytState.forsikringIdentifikator,
                kunde.kundeinfo.forsikringer,
            );

            if (bygningforsikring !== null) {
                const objekter = bygningforsikring.objekter.map((objekt) => {
                    const besvartValg = currentSporsmalObjekter.find(
                        (o) => o.objektnummer === objekt.objektnummer && o.objektnavn === objekt.visningsTekst,
                    );

                    return {
                        objektnavn: objekt.visningsTekst,
                        objektnummer: objekt.objektnummer,
                        checked: besvartValg ? besvartValg.checked : false,
                    };
                });

                addOrUpdatePakrevdAnnenBygningInntil10kvmAlternativ(currentSporsmalObjekter, objekter);
                dispatch(updateObjekterForForsikring(objekter));
            } else dispatch(updateObjekterForForsikring([]));
            setValgtForsikringWithObjekter(bygningforsikring);
        }
    }, [sporsmalsflytState.forsikringIdentifikator, kunde.kundeinfo, currentSporsmalObjekter, dispatch]);

    const onChange = (checked: boolean, value: string) => {
        dispatch(updateValgteObjekter(parseInt(value, 10)));
    };

    return (
        <div className="container__standard-width">
            {valgtForsikringWithObjekter && (
                <CheckboxGroup
                    className="container__standard-width"
                    label={currentSporsmal.sporsmal.tekst}
                    sublabel={currentSporsmal.sporsmal.ingress}
                    options={getForsikringElementerAsAlternativerOrUpdatedCurrentSporsmalAlternativer(
                        valgtForsikringWithObjekter,
                        currentSporsmal,
                        onChange,
                    )}
                    key={`forsikring-objekter-checkbox-sporsmal-${currentSporsmal.sporsmal.id}`}
                />
            )}
            {showFeilmelding && <ObjekterCheckboxFeilmelding objekter={currentSporsmal.objekter} />}
        </div>
    );
};
